import { videos } from "@/mocks/videos";
import type { VideoItem } from "@/types/video";

const allVideos = videos as VideoItem[];

export const REGION_OPTIONS = ["全部", "中国", "美国", "日本", "韩国", "英国"];
export const YEAR_OPTIONS = ["全部", "2024", "2023", "2022", "更早"];
export const GENRE_OPTIONS = [
  "全部",
  "动作",
  "科幻",
  "悬疑",
  "犯罪",
  "剧情",
  "爱情",
  "家庭",
  "历史",
  "古装",
  "奇幻",
  "冒险",
  "都市",
  "励志",
  "惊悚",
  "科技",
  "年代",
  "真人秀",
  "亲子",
  "音乐",
  "选秀",
  "竞技",
  "脱口秀",
  "喜剧",
  "美食",
  "纪实",
  "旅行",
  "科普",
];

export type SortKey = "recommend" | "latest" | "rating";

export const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "recommend", label: "默认推荐" },
  { key: "latest", label: "最新更新" },
  { key: "rating", label: "评分最高" },
];

export interface FilterState {
  region: string;
  year: string;
  genre: string;
}

export const DEFAULT_FILTERS: FilterState = { region: "全部", year: "全部", genre: "全部" };

export function getAllVideos(): VideoItem[] {
  return allVideos;
}

export function getVideosByType(type: string): VideoItem[] {
  return allVideos.filter((item) => item.type === type);
}

export function getVideoById(id: string): VideoItem | undefined {
  return allVideos.find((item) => item.id === id);
}

export function getFeatured(limit = 6): VideoItem[] {
  const featured = allVideos.filter((item) => item.featured);
  return featured.slice(0, limit);
}

export function getLatest(limit = 8): VideoItem[] {
  return [...allVideos]
    .sort((a, b) => (a.updateDate < b.updateDate ? 1 : -1))
    .slice(0, limit);
}

export function getHot(limit = 8): VideoItem[] {
  return [...allVideos].sort((a, b) => b.views - a.views).slice(0, limit);
}

function matchYear(year: number, option: string): boolean {
  if (option === "全部") return true;
  if (option === "更早") return year <= 2021;
  return year === Number(option);
}

export function applyFilters(list: VideoItem[], filters: FilterState): VideoItem[] {
  return list.filter((item) => {
    if (filters.region !== "全部" && item.region !== filters.region) return false;
    if (!matchYear(item.year, filters.year)) return false;
    if (filters.genre !== "全部" && !item.genres.includes(filters.genre)) return false;
    return true;
  });
}

export function sortVideos(list: VideoItem[], key: SortKey): VideoItem[] {
  const copy = [...list];
  if (key === "latest") {
    return copy.sort((a, b) => (a.updateDate < b.updateDate ? 1 : -1));
  }
  if (key === "rating") {
    return copy.sort((a, b) => b.rating - a.rating);
  }
  // recommend: featured first, then by views
  return copy.sort((a, b) => {
    if (a.featured !== b.featured) return a.featured ? -1 : 1;
    return b.views - a.views;
  });
}

export function searchVideos(keyword: string): VideoItem[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return allVideos.filter((item) => {
    const haystack = [
      item.title,
      item.director,
      item.type === "movie" ? "电影" : item.type === "tv" ? "电视剧" : "综艺",
      ...item.actors,
      ...item.genres,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export function getRelated(video: VideoItem, limit = 5): VideoItem[] {
  const sameType = allVideos.filter((item) => item.id !== video.id && item.type === video.type);
  const sameGenre = sameType.filter((item) =>
    item.genres.some((g) => video.genres.includes(g))
  );
  const rest = sameType.filter((item) => !sameGenre.includes(item));
  return [...sameGenre, ...rest].slice(0, limit);
}

export function paginate<T>(list: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

export function totalPages(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}